import { useState, useEffect } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useImportTableViewModel } from "./ImportTable.vm";
import { JobsImportTableProps } from "./ImportTable.types";
import { EditModeProvider } from "./shared/hooks/EditModeContext";
import { useResponsiveView } from "./shared/hooks/useResponsiveView";
import { ImportTableMobile } from "./mobile/ImportTable.mobile";
import { ImportTableDesktop } from "./desktop/ImportTable.desktop";

// Wrapper component that defers table mount to prevent UI freeze
export function JobImportTable(props: JobsImportTableProps) {
	const [isReady, setIsReady] = useState(false);

	useEffect(() => {
		const timeoutId = requestAnimationFrame(() => {
			setIsReady(true);
		});
		return () => {
			cancelAnimationFrame(timeoutId);
		};
	}, []);

	if (!isReady) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200, width: "100%" }}>
				<Typography>Loading...</Typography>
			</Box>
		);
	}

	return (
		<EditModeProvider>
			<JobImportTableInner {...props} />
		</EditModeProvider>
	);
}

function JobImportTableInner(props: JobsImportTableProps) {
	const vm = useImportTableViewModel(props);
	const { isMobile } = useResponsiveView();

	// Show loading overlay while processing jobs (deal matching and validation)
	if (vm.isInitializing) {
		return (
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					justifyContent: "center",
					alignItems: "center",
					minHeight: 400,
					width: "100%",
					bgcolor: "background.paper",
					borderRadius: 1,
					p: 4,
				}}
			>
				<CircularProgress size={48} />
				<Typography variant="h6" sx={{ mt: 2 }}>
					Processing {props.jobs.length} jobs...
				</Typography>
			</Box>
		);
	}

	if (isMobile) {
		return (
			<ImportTableMobile
				viewModel={vm}
				props={props}
				mapDisplayMode={props.mapDisplayMode}
				setMapDisplayMode={props.setMapDisplayMode}
				selectedFeatureIds={props.selectedFeatureIds}
				onClearFeatureSelection={props.onClearFeatureSelection}
			/>
		);
	}

	return <ImportTableDesktop vm={vm} props={props} />;
}
